(function() {
  const SELECTOR = '[data-colorful-header]';
  const MEDIA_QUERY = '(min-width: 1024px)';
  const FOCUSABLE_SELECTORS = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(', ');
  const instances = new Map();
  const DEFAULT_DRAWER_VIEW = 'cart';

  function ensureGlobalDrawerBridge() {
    if (typeof window === 'undefined') {
      return null;
    }
    if (!window.CFS_DRAWER) {
      window.CFS_DRAWER = {
        enabled: false,
        open() {},
        refreshCart() {
          return Promise.resolve();
        }
      };
    }
    return window.CFS_DRAWER;
  }

  function getPrimaryDrawerInstance() {
    for (const instance of instances.values()) {
      if (instance && instance.drawer) {
        return instance;
      }
    }
    const iterator = instances.values();
    const first = iterator.next();
    return first && !first.done ? first.value : null;
  }

  function updateGlobalDrawerBridge() {
    const bridge = ensureGlobalDrawerBridge();
    if (!bridge) return;

    bridge.enabled = instances.size > 0;

    bridge.open = function(view = DEFAULT_DRAWER_VIEW) {
      const instance = getPrimaryDrawerInstance();
      if (!instance) return;
      const targetView = view || DEFAULT_DRAWER_VIEW;
      if (targetView === 'cart' && !instance.drawer) {
        return;
      }
      instance.openDrawer(targetView, null);
    };

    bridge.refreshCart = function(force, productTitle, cartState) {
      const instance = getPrimaryDrawerInstance();
      if (!instance) {
        return Promise.resolve();
      }
      return instance.refreshCartDrawer(cartState || null);
    };
  }

  ensureGlobalDrawerBridge();
  const MEGA_VIEWPORT_GUTTER = 24;
  const SUPPORTS_BACKDROP_FILTER = (() => {
    if (typeof window === 'undefined') {
      return true;
    }
    const css = window.CSS;
    if (!css || typeof css.supports !== 'function') {
      return true;
    }
    const standard = css.supports('backdrop-filter', 'blur(1px)');
    const webkit = css.supports('-webkit-backdrop-filter', 'blur(1px)');
    return Boolean(standard || webkit);
  })();

  if (typeof document !== 'undefined' && document.documentElement && document.documentElement.classList) {
    document.documentElement.classList.toggle('colorful-header--no-backdrop', !SUPPORTS_BACKDROP_FILTER);
  }

  function syncOverlayIndicatorClasses() {
    if (typeof document === 'undefined') {
      return;
    }
    let overlayActive = false;
    let blurActive = false;
    instances.forEach((instance) => {
      if (!instance) return;
      if (!overlayActive && (instance.isDrawerOpen || instance.isMegaOpen || instance.isNavOpen)) {
        overlayActive = true;
      }
      if (!blurActive && (instance.isDrawerOpen || instance.isMegaOpen)) {
        blurActive = true;
      }
    });

    const root = document.documentElement;
    if (root && root.classList) {
      root.classList.toggle('colorful-header--overlay-active', overlayActive);
      root.classList.toggle('colorful-header--blur-active', blurActive);
    }

    const body = document.body;
    if (body && body.classList) {
      body.classList.toggle('colorful-header--overlay-active', overlayActive);
      body.classList.toggle('colorful-header--blur-active', blurActive);
    }
  }
  const DEFAULT_HEADER_HEIGHT = '72px';
  const DEFAULT_HEADER_HEIGHT_NUMERIC = parseFloat(DEFAULT_HEADER_HEIGHT) || 72;
  const OVERLAP_SECTION_KEYWORDS = [
    'banner',
    'slider',
    'slide-show',
    'slideshow',
    'image',
    'hero',
    'lookbook',
    'gallery',
    'video',
    'spotlight',
    'main-article',
    'collection-banner',
    'pagetify',
    'pf-',
    'ai-hero'
  ];
  const OVERLAP_CHILD_KEYWORDS = [
    'banner',
    'slider',
    'slideshow',
    'hero',
    'lookbook',
    'gallery',
    'video',
    'spotlight'
  ];
  const OVERLAP_MEDIA_SELECTOR = [
    '[data-header-overlap]',
    '.slider-style-image-banner',
    '.slideshow-wrapper',
    '.halo-banner',
    '.image-banner',
    '.custom-image-banner',
    '.puzzled-image-banner',
    '.counter-positions-image-banner',
    '.brand-slider',
    '.collection-slider',
    '.slidable-spotlight',
    '.spotlight-block',
    '.large-lookbook-banners',
    '.lookbook-with-collection',
    '.article-hero',
    '.article-hero-wrapper',
    '.ai-hero-section',
    '.collection-banner',
    '.collection-banner-adv',
    '.custom-press-banner',
    '.video-block',
    '.video-banner'
  ].join(', ');

  class ColorfulHeader {
    constructor(section) {
      this.section = section;
      this.id = section.dataset.sectionId || section.dataset.colorfulHeader;
      this.mql = window.matchMedia(MEDIA_QUERY);
      this.navToggle = section.querySelector('[data-nav-toggle]');
      this.navBackdrop = section.querySelector('[data-nav-backdrop]');
      this.navSheet = section.querySelector('[data-nav-sheet]');
      this.container = section.querySelector('.colorful-header__container');
      this.actions = section.querySelector('.colorful-header__actions');
      this.drawer = section.querySelector('[data-header-drawer]');
      this.drawerClose = section.querySelector('[data-drawer-close]');
      this.drawerTriggers = Array.from(section.querySelectorAll('[data-drawer-trigger]'));
      this.drawerViews = Array.from(section.querySelectorAll('[data-drawer-view]'));
      this.overlay = section.querySelector('[data-drawer-overlay]');
      this.drawerHeadingFragments = Array.from(section.querySelectorAll('[data-drawer-label]'));
      this.searchForm = this.drawer ? this.drawer.querySelector('.colorful-header__search-form') : null;
      this.suggestionLinks = this.drawer ? Array.from(this.drawer.querySelectorAll('.colorful-header__suggestion-link')) : [];
      this.megaItems = Array.from(section.querySelectorAll('[data-mega-item]'));
      this.activeTrigger = null;
      this.activeView = null;
      this.isDrawerOpen = false;
      this.isNavOpen = false;
      this.escapeAttached = false;
      this.megaConfigs = [];
      this.isMegaOpen = false;
      this.focusTrapAttached = false;
      this.drawerFocusables = [];
      this.drawerNeedsTabIndex = false;
      this.lastOverlapState = null;
      this.cartForms = [];
      this.upsellForms = [];
      this.isUpdatingCartQuantity = false;
      this.isAddingUpsell = false;
      this.isRenderingCart = false;
      this.pendingCartRender = null;
      this.headerGroup = section.closest('.header-group');
      this.lastDrawerOffset = null;
      this.lastHeaderHeight = DEFAULT_HEADER_HEIGHT_NUMERIC;
      this.lastAnnouncementHeight = 0;
      this.lastStackHeight = null;
      this.bodyStackApplied = false;
      this.lastOverlayBlur = null;
      this.lastOverlayColor = null;
      this.lastOverlayOpacity = null;
      this.lastNavOverlayOpacity = null;
      this.lastOverlayBackground = null;
      this.lastNavOverlayBackground = null;

      this.onNavToggle = this.onNavToggle.bind(this);
      this.onNavBackdrop = this.onNavBackdrop.bind(this);
      this.onDrawerTrigger = this.onDrawerTrigger.bind(this);
      this.onDrawerClose = this.onDrawerClose.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onDrawerKeyDown = this.onDrawerKeyDown.bind(this);
      this.onSuggestionClick = this.onSuggestionClick.bind(this);
      this.onCartQuantityButtonClick = this.onCartQuantityButtonClick.bind(this);
      this.onCartQuantitySubmit = this.onCartQuantitySubmit.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onUpsellFormSubmit = this.onUpsellFormSubmit.bind(this);

      this.init();
    }

    init() {
      this.setHeaderHeight();
      window.addEventListener('resize', this.onResize);
      if (typeof this.mql.addEventListener === 'function') {
        this.mql.addEventListener('change', this.onMediaChange);
      } else if (typeof this.mql.addListener === 'function') {
        this.mql.addListener(this.onMediaChange);
      }

      this.initNav();
      this.initDrawer();
      this.initCartFeatures();
      this.initMegaMenus();
      this.initSearchSuggestions();
      document.addEventListener('cart-update', this.onCartUpdate);
      this.syncScrollLockState(this.isDrawerOpen || this.isNavOpen);
    }

    initNav() {
      if (!this.navToggle || !this.navSheet) return;
      this.navToggle.addEventListener('click', this.onNavToggle);
      this.navToggle.setAttribute('aria-expanded', 'false');
      this.navToggle.setAttribute('aria-pressed', 'false');
      if (this.navBackdrop) {
        this.navBackdrop.addEventListener('click', this.onNavBackdrop);
      }
    }

    initDrawer() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.onDrawerTrigger);
        trigger.setAttribute('aria-pressed', 'false');
        trigger.setAttribute('aria-expanded', 'false');
      });
      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', this.onDrawerClose);
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', this.onOverlayClick);
      }
    }

    initCartFeatures() {
      if (!this.drawer) return;
      this.detachCartFeatures();
      this.cartForms = Array.from(this.drawer.querySelectorAll('[data-cart-quantity-form]'));
      this.upsellForms = Array.from(this.drawer.querySelectorAll('.colorful-header__upsell-form'));

      this.cartForms.forEach((form) => {
        form.addEventListener('submit', this.onCartQuantitySubmit);
        const buttons = Array.from(form.querySelectorAll('[data-cart-quantity-button]'));
        buttons.forEach((button) => {
          button.addEventListener('click', this.onCartQuantityButtonClick);
        });
      });

      this.upsellForms.forEach((form) => {
        form.addEventListener('submit', this.onUpsellFormSubmit);
      });
    }

    detachCartFeatures() {
      if (this.cartForms && this.cartForms.length) {
        this.cartForms.forEach((form) => {
          form.removeEventListener('submit', this.onCartQuantitySubmit);
          const buttons = form.querySelectorAll('[data-cart-quantity-button]');
          buttons.forEach((button) => {
            button.removeEventListener('click', this.onCartQuantityButtonClick);
          });
          delete form.dataset.quantityTarget;
          delete form._activeSubmitButton;
        });
      }
      this.cartForms = [];

      if (this.upsellForms && this.upsellForms.length) {
        this.upsellForms.forEach((form) => {
          form.removeEventListener('submit', this.onUpsellFormSubmit);
        });
      }
      this.upsellForms = [];
    }

    onCartQuantityButtonClick(event) {
      const button = event.currentTarget;
      const form = button.closest('[data-cart-quantity-form]');
      if (!form) return;
      form.dataset.quantityTarget = button.dataset.quantityTarget || button.value || '';
      form._activeSubmitButton = button;
    }

    onCartQuantitySubmit(event) {
      event.preventDefault();
      if (this.isUpdatingCartQuantity) return;
      const form = event.currentTarget;
      const submitter = event.submitter || form._activeSubmitButton || null;
      const keyInput = form.querySelector('input[name="id"]');
      if (!keyInput) return;
      const cartKey = keyInput.value;
      if (!cartKey) return;
      const quantityValue = form.dataset.quantityTarget || (submitter && (submitter.dataset.quantityTarget || submitter.value));
      const quantity = Number(quantityValue);
      if (Number.isNaN(quantity)) return;

      this.isUpdatingCartQuantity = true;
      form.classList.add('is-updating');
      if (submitter) {
        submitter.disabled = true;
        submitter.setAttribute('aria-busy', 'true');
      }

      const changeUrlBase = window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change';
      const changeUrl = `${changeUrlBase}.js`;

      fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: cartKey, quantity })
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to update cart quantity');
          }
          return response.json();
        })
        .then((cart) => {
          if (window.halo && typeof window.halo.updateSidebarCart === 'function') {
            window.halo.updateSidebarCart(cart);
          } else {
            this.refreshCartDrawer(cart);
            document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.isUpdatingCartQuantity = false;
          form.classList.remove('is-updating');
          if (submitter) {
            submitter.disabled = false;
            submitter.removeAttribute('aria-busy');
          }
          delete form.dataset.quantityTarget;
          delete form._activeSubmitButton;
        });
    }

    onUpsellFormSubmit(event) {
      event.preventDefault();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      } else if (typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }
      if (this.isAddingUpsell) return;

      const form = event.currentTarget;
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      this.isAddingUpsell = true;
      form.classList.add('is-submitting');
      if (submitter) {
        submitter.disabled = true;
        submitter.setAttribute('aria-busy', 'true');
      }

      const formData = new FormData(form);
      const addBaseUrl = window.routes && typeof window.routes.cart_add_url === 'string' ? window.routes.cart_add_url : '/cart/add';
      const addUrl = addBaseUrl.endsWith('.js') ? addBaseUrl : `${addBaseUrl}.js`;
      const cartBaseUrl = window.routes && typeof window.routes.cart === 'string' ? window.routes.cart : '/cart';
      const cartUrl = cartBaseUrl.endsWith('.js') ? cartBaseUrl : `${cartBaseUrl}.js`;

      fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to add item to cart');
          }
          return response.json();
        })
        .then(() => fetch(cartUrl, { headers: { Accept: 'application/json' } }))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to load cart state');
          }
          return response.json();
        })
        .then((cart) => {
          if (window.halo && typeof window.halo.updateSidebarCart === 'function') {
            window.halo.updateSidebarCart(cart);
          } else {
            this.refreshCartDrawer(cart);
            document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.isAddingUpsell = false;
          form.classList.remove('is-submitting');
          if (submitter) {
            submitter.disabled = false;
            submitter.removeAttribute('aria-busy');
          }
        });
    }

    onCartUpdate(event) {
      this.refreshCartDrawer(event && event.detail ? event.detail : null);
    }

    refreshCartDrawer(cart) {
      if (!this.drawer) return Promise.resolve();
      const sectionId = this.section.dataset.sectionId || this.section.dataset.colorfulHeader;
      if (!sectionId) return Promise.resolve();
      if (this.isRenderingCart) {
        this.pendingCartRender = cart;
        return Promise.resolve();
      }

      this.isRenderingCart = true;
      const baseUrl = window.routes && typeof window.routes.root === 'string' ? window.routes.root : '';
      const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const url = `${normalizedBase}/?sections=${encodeURIComponent(sectionId)}`;

      const request = fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to load cart drawer content');
          }
          return response.json();
        })
        .then((data) => {
          const html = data && data[sectionId];
          if (!html) return;
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newSection = doc.querySelector(SELECTOR);
          if (!newSection) return;

          const currentTrigger = this.section.querySelector('[data-drawer-trigger="cart"]');
          const newTrigger = newSection.querySelector('[data-drawer-trigger="cart"]');
          if (currentTrigger && newTrigger) {
            const existingBadge = currentTrigger.querySelector('.colorful-header__badge');
            const newBadge = newTrigger.querySelector('.colorful-header__badge');
            if (existingBadge && !newBadge) {
              existingBadge.remove();
            } else if (!existingBadge && newBadge) {
              currentTrigger.appendChild(newBadge.cloneNode(true));
            } else if (existingBadge && newBadge) {
              existingBadge.replaceWith(newBadge.cloneNode(true));
            }
          }

          const newLabels = newSection.querySelectorAll('[data-drawer-label]');
          newLabels.forEach((label) => {
            const target = label.dataset.drawerLabel;
            if (!target) return;
            const current = this.section.querySelector(`[data-drawer-label="${target}"]`);
            if (current) {
              current.innerHTML = label.innerHTML;
              const isHidden = label.hasAttribute('hidden');
              if (isHidden) {
                current.hidden = true;
                current.setAttribute('hidden', label.getAttribute('hidden'));
              } else {
                current.hidden = false;
                current.removeAttribute('hidden');
              }
            }
          });

          const newCounts = newSection.querySelectorAll('[data-cart-count]');
          const currentCounts = this.section.querySelectorAll('[data-cart-count]');
          currentCounts.forEach((element, index) => {
            const replacement = newCounts[index];
            if (!replacement) return;
            element.innerHTML = replacement.innerHTML;
            const isHidden = replacement.hasAttribute('hidden');
            if (isHidden) {
              element.hidden = true;
              element.setAttribute('hidden', replacement.getAttribute('hidden'));
            } else {
              element.hidden = false;
              element.removeAttribute('hidden');
            }
          });

          const currentCartView = this.drawer.querySelector('[data-drawer-view="cart"]');
          const newCartView = newSection.querySelector('[data-drawer-view="cart"]');
          if (currentCartView && newCartView) {
            const wasHidden = currentCartView.hasAttribute('hidden') || currentCartView.hidden;
            currentCartView.innerHTML = newCartView.innerHTML;
            if (wasHidden) {
              currentCartView.hidden = true;
              currentCartView.setAttribute('hidden', '');
            } else {
              currentCartView.hidden = false;
              currentCartView.removeAttribute('hidden');
            }
          }

          this.drawerHeadingFragments = Array.from(this.section.querySelectorAll('[data-drawer-label]'));
          this.drawerFocusables = [];
          this.initCartFeatures();
          if (this.focusTrapAttached) {
            this.updateDrawerFocusables();
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.isRenderingCart = false;
          if (this.pendingCartRender) {
            const pending = this.pendingCartRender;
            this.pendingCartRender = null;
            this.refreshCartDrawer(pending);
          }
        });
      return request;
    }

    initMegaMenus() {
      if (!this.megaItems.length) return;
      this.megaConfigs = [];
      this.megaItems.forEach((item, index) => {
        const trigger = item.querySelector('[data-mega-toggle]');
        const panel = item.querySelector('[data-mega-panel]');
        if (!trigger || !panel) return;

        const controlId = `${this.id}-mega-${index}`;
        panel.id = controlId;
        trigger.setAttribute('aria-controls', controlId);
        trigger.setAttribute('aria-expanded', 'false');

        const clickHandler = (event) => {
          const open = item.classList.contains('is-open');
          if (open) {
            this.closeMega(item);
          } else {
            event.preventDefault();
            this.openMega(item);
          }
        };

        const keyHandler = (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeMega(item);
            trigger.focus();
          }
        };

        const focusOut = (event) => {
          if (!item.contains(event.relatedTarget)) {
            this.closeMega(item);
          }
        };

        const hoverHandler = (event) => {
          if (!this.mql.matches) return;
          if (event && event.type === 'mouseenter') {
            this.openMega(item);
          }
        };

        const leaveHandler = (event) => {
          if (!this.mql.matches) return;
          const related = event && event.relatedTarget;
          if (related && item.contains(related)) return;
          this.closeMega(item);
        };

        const focusHandler = () => {
          if (!this.mql.matches) return;
          this.openMega(item);
        };

        trigger.addEventListener('click', clickHandler);
        trigger.addEventListener('keydown', keyHandler);
        trigger.addEventListener('focus', focusHandler);
        item.addEventListener('mouseenter', hoverHandler);
        item.addEventListener('mouseleave', leaveHandler);
        panel.addEventListener('focusout', focusOut);

        this.megaConfigs.push({
          item,
          trigger,
          panel,
          clickHandler,
          keyHandler,
          focusOut,
          hoverHandler,
          leaveHandler,
          focusHandler
        });
      });

      if (this.megaConfigs.length) {
        document.addEventListener('click', this.onDocumentClick);
      }
    }

    initSearchSuggestions() {
      if (!this.suggestionLinks.length) return;
      this.suggestionLinks.forEach((link) => {
        link.addEventListener('click', this.onSuggestionClick);
      });
    }

    onSuggestionClick(event) {
      const link = event.currentTarget;
      if (!(link instanceof HTMLElement)) return;

      const rawSuggestion = link.dataset && link.dataset.suggestion ? link.dataset.suggestion : link.textContent || '';
      const suggestion = rawSuggestion.trim();
      if (!suggestion) return;

      if (this.searchForm) {
        event.preventDefault();
        const actionAttribute = this.searchForm.getAttribute('action');
        const action = actionAttribute && actionAttribute.trim() !== '' ? actionAttribute : this.searchForm.action;
        const targetUrl = new URL(action || window.location.pathname, window.location.origin);
        const formData = new FormData(this.searchForm);
        formData.set('q', suggestion);
        const params = new URLSearchParams();
        formData.forEach((value, key) => {
          if (typeof value === 'string') {
            params.set(key, value);
          }
        });
        targetUrl.search = params.toString();

        const searchInput = this.searchForm.querySelector('input[name="q"]');
        if (searchInput) {
          searchInput.value = suggestion;
        }

        window.location.assign(targetUrl.toString());
        return;
      }

      const href = link.getAttribute('href') || link.href;
      if (!href) return;

      event.preventDefault();
      const fallbackUrl = new URL(href, window.location.origin);
      fallbackUrl.searchParams.set('q', suggestion);
      window.location.assign(fallbackUrl.toString());
    }

    onNavToggle(event) {
      event.preventDefault();
      if (this.isNavOpen) {
        this.closeNav();
      } else {
        this.openNav();
      }
    }

    onNavBackdrop() {
      this.closeNav();
    }

    onDrawerTrigger(event) {
      const trigger = event.currentTarget;
      const target = trigger.getAttribute('data-drawer-trigger');
      if (!target) return;
      if (this.isDrawerOpen && this.activeView === target) {
        this.closeDrawer();
      } else {
        event.preventDefault();
        this.openDrawer(target, trigger);
      }
    }

    onDrawerClose(event) {
      event.preventDefault();
      this.closeDrawer();
    }

    onOverlayClick() {
      this.closeDrawer();
      this.closeNav();
      if (this.isMegaOpen) {
        this.megaConfigs.forEach(({ item }) => this.closeMega(item));
      }
    }

    onDocumentClick(event) {
      if (!this.section.contains(event.target)) {
        this.megaConfigs.forEach(({ item }) => this.closeMega(item));
      }
    }

    onKeyUp(event) {
      if (event.key !== 'Escape') return;
      if (this.isDrawerOpen) {
        this.closeDrawer();
      } else if (this.isNavOpen) {
        this.closeNav();
      } else {
        this.megaConfigs.forEach(({ item }) => this.closeMega(item));
      }
    }

    onResize() {
      this.setHeaderHeight();
      if (this.mql.matches && this.isNavOpen) {
        this.closeNav();
      }
      if (this.isMegaOpen) {
        this.megaConfigs.forEach(({ item }) => {
          if (item.classList.contains('is-open')) {
            this.updateMegaPosition(item);
          }
        });
      }
    }

    onMediaChange() {
      if (this.mql.matches) {
        this.closeNav();
      }
      if (this.isMegaOpen) {
        this.megaConfigs.forEach(({ item }) => {
          if (item.classList.contains('is-open')) {
            this.updateMegaPosition(item);
          }
        });
      }
    }

    openNav() {
      if (this.isDrawerOpen) this.closeDrawer();
      if (this.isMegaOpen) {
        this.megaConfigs.forEach(({ item }) => this.closeMega(item));
      }
      this.isNavOpen = true;
      this.section.dataset.navOpen = 'true';
      this.navToggle.setAttribute('aria-expanded', 'true');
      this.navToggle.setAttribute('aria-pressed', 'true');
      this.updateOverlayState();
      this.addEscapeListener();
      this.setHeaderHeight();
    }

    closeNav() {
      if (!this.isNavOpen) return;
      this.isNavOpen = false;
      this.section.dataset.navOpen = 'false';
      if (this.navToggle) {
        this.navToggle.setAttribute('aria-expanded', 'false');
        this.navToggle.setAttribute('aria-pressed', 'false');
      }
      this.updateOverlayState();
      this.removeEscapeListener();
      this.setHeaderHeight();
    }

    openDrawer(view, trigger) {
      if (!this.drawer) return;
      if (this.isNavOpen) this.closeNav();
      if (this.isMegaOpen) {
        this.megaConfigs.forEach(({ item }) => this.closeMega(item));
      }
      this.isDrawerOpen = true;
      this.activeView = view;
      this.activeTrigger = trigger;
      this.section.classList.add('colorful-header--drawer-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.dataset.activeView = view;
      this.drawerTriggers.forEach((btn) => {
        const match = btn.getAttribute('data-drawer-trigger');
        const pressed = match === view;
        btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        btn.setAttribute('aria-expanded', pressed ? 'true' : 'false');
      });

      if (this.drawerHeadingFragments.length) {
        this.drawerHeadingFragments.forEach((fragment) => {
          const match = fragment.getAttribute('data-drawer-label');
          const isMatch = match === view;
          fragment.hidden = !isMatch;
          if (isMatch) {
            fragment.removeAttribute('hidden');
          } else {
            fragment.setAttribute('hidden', '');
          }
        });
      }

      this.drawerViews.forEach((panel) => {
        const isCurrent = panel.dataset.drawerView === view;
        panel.hidden = !isCurrent;
        if (isCurrent) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      this.updateDrawerFocusables();
      const initial = this.drawer.querySelector(`[data-initial-focus="${view}"]`) || this.drawer.querySelector('[data-initial-focus]');
      requestAnimationFrame(() => {
        if (initial) {
          this.focusWithoutScroll(initial);
        }
      });

      this.updateOverlayState();
      this.addEscapeListener();
      this.attachFocusTrap();
      this.setHeaderHeight();
    }

    closeDrawer() {
      if (!this.isDrawerOpen || !this.drawer) return;
      this.isDrawerOpen = false;
      this.drawer.setAttribute('aria-hidden', 'true');
      delete this.drawer.dataset.activeView;
      this.section.classList.remove('colorful-header--drawer-open');
      this.drawerTriggers.forEach((btn) => {
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-expanded', 'false');
      });
      if (this.drawerHeadingFragments.length) {
        this.drawerHeadingFragments.forEach((fragment, index) => {
          const isPrimary = index === 0;
          fragment.hidden = !isPrimary;
          if (isPrimary) {
            fragment.removeAttribute('hidden');
          } else {
            fragment.setAttribute('hidden', '');
          }
        });
      }
      const returnFocus = this.activeTrigger;
      this.activeTrigger = null;
      this.activeView = null;
      this.detachFocusTrap();
      this.updateOverlayState();
      this.removeEscapeListener();
      this.setHeaderHeight();
      if (returnFocus) {
        requestAnimationFrame(() => {
          this.focusWithoutScroll(returnFocus);
        });
      }
    }

    focusWithoutScroll(element) {
      if (!element || typeof element.focus !== 'function') return;
      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
      }
    }

    updateDrawerFocusables() {
      if (!this.drawer) {
        this.drawerFocusables = [];
        return this.drawerFocusables;
      }
      const nodes = Array.from(this.drawer.querySelectorAll(FOCUSABLE_SELECTORS));
      const visible = nodes.filter((element) => {
        if (element.closest('[hidden]')) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        if (element.hasAttribute('disabled')) return false;
        if (element.hasAttribute('tabindex') && element.tabIndex < 0) return false;
        const style = window.getComputedStyle(element);
        if (style.visibility === 'hidden' || style.display === 'none') return false;
        if (!element.getClientRects().length && element.offsetWidth === 0 && element.offsetHeight === 0) return false;
        return true;
      });
      this.drawerFocusables = visible;
      return this.drawerFocusables;
    }

    attachFocusTrap() {
      if (this.focusTrapAttached || !this.drawer) return;
      if (!this.drawer.hasAttribute('tabindex')) {
        this.drawer.setAttribute('tabindex', '-1');
        this.drawerNeedsTabIndex = true;
      }
      document.addEventListener('focusin', this.onFocusIn);
      this.drawer.addEventListener('keydown', this.onDrawerKeyDown);
      this.focusTrapAttached = true;
    }

    detachFocusTrap() {
      if (!this.focusTrapAttached || !this.drawer) {
        this.drawerFocusables = [];
        return;
      }
      document.removeEventListener('focusin', this.onFocusIn);
      this.drawer.removeEventListener('keydown', this.onDrawerKeyDown);
      if (this.drawerNeedsTabIndex) {
        this.drawer.removeAttribute('tabindex');
        this.drawerNeedsTabIndex = false;
      }
      this.focusTrapAttached = false;
      this.drawerFocusables = [];
    }

    onFocusIn(event) {
      if (!this.isDrawerOpen || !this.drawer) return;
      if (this.drawer.contains(event.target)) return;
      const focusables = this.updateDrawerFocusables();
      const fallback = focusables[0] || this.drawer;
      if (fallback && typeof fallback.focus === 'function') {
        const applyFocus = () => this.focusWithoutScroll(fallback);
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(applyFocus);
        } else {
          applyFocus();
        }
      }
    }

    onDrawerKeyDown(event) {
      if (!this.isDrawerOpen || event.key !== 'Tab') return;
      const focusables = this.updateDrawerFocusables();
      if (!focusables.length) {
        event.preventDefault();
        if (this.drawer && typeof this.drawer.focus === 'function') {
          this.focusWithoutScroll(this.drawer);
        }
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = this.drawer.contains(document.activeElement) ? document.activeElement : null;
      const currentIndex = focusables.indexOf(active);
      if (event.shiftKey) {
        if (currentIndex <= 0) {
          event.preventDefault();
          this.focusWithoutScroll(last);
        }
        return;
      }
      if (currentIndex === -1 || currentIndex === focusables.length - 1) {
        event.preventDefault();
        this.focusWithoutScroll(first);
      }
    }

    openMega(item) {
      if (this.isDrawerOpen) {
        this.closeDrawer();
      }
      if (this.isNavOpen) {
        this.closeNav();
      }
      this.megaConfigs.forEach(({ item: other }) => {
        if (other !== item) this.closeMega(other);
      });
      this.updateMegaPosition(item);
      item.classList.add('is-open');
      const trigger = item.querySelector('[data-mega-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.section.classList.add('colorful-header--mega-open');
      this.isMegaOpen = true;
      this.updateOverlayState();
      this.addEscapeListener();
    }

    closeMega(item) {
      if (!item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-mega-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      const config = this.megaConfigs.find(({ item: candidate }) => candidate === item);
      const panel = config && config.panel ? config.panel : item.querySelector('[data-mega-panel]');
      if (panel) {
        panel.style.removeProperty('--mega-shift');
      }
      const anyOpen = this.megaItems.some((megaItem) => megaItem.classList.contains('is-open'));
      if (!anyOpen) {
        this.section.classList.remove('colorful-header--mega-open');
      }
      this.isMegaOpen = anyOpen;
      this.updateOverlayState();
      if (!anyOpen) {
        this.removeEscapeListener();
      }
    }

    updateMegaPosition(item) {
      if (!item) return;
      const config = this.megaConfigs.find(({ item: candidate }) => candidate === item);
      const panel = config && config.panel ? config.panel : item.querySelector('[data-mega-panel]');
      const trigger = config && config.trigger ? config.trigger : item.querySelector('[data-mega-toggle]');
      if (!panel || !trigger) return;

      if (!this.mql.matches) {
        panel.style.removeProperty('--mega-shift');
        return;
      }

      let viewportWidth = 0;
      if (typeof window !== 'undefined' && typeof window.innerWidth === 'number') {
        viewportWidth = window.innerWidth;
      } else if (typeof document !== 'undefined' && document.documentElement) {
        viewportWidth = document.documentElement.clientWidth || 0;
      }

      if (!viewportWidth) {
        panel.style.removeProperty('--mega-shift');
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const itemCenter = triggerRect.left + triggerRect.width / 2;
      const desiredCenter = viewportWidth / 2;
      let shift = desiredCenter - itemCenter;

      const panelWidth = panel.offsetWidth || panel.getBoundingClientRect().width || 0;
      if (panelWidth) {
        const halfWidth = panelWidth / 2;
        const minShift = MEGA_VIEWPORT_GUTTER + halfWidth - itemCenter;
        const maxShift = viewportWidth - MEGA_VIEWPORT_GUTTER - halfWidth - itemCenter;
        if (minShift <= maxShift) {
          shift = Math.min(Math.max(shift, minShift), maxShift);
        } else {
          shift = desiredCenter - itemCenter;
        }
      }

      if (Number.isFinite(shift)) {
        panel.style.setProperty('--mega-shift', `${shift}px`);
      } else {
        panel.style.removeProperty('--mega-shift');
      }
    }

    updateOverlayState() {
      const megaActive = this.isMegaOpen;
      const overlayActive = this.isDrawerOpen || megaActive;
      const announcementActive = overlayActive || this.isNavOpen;
      if (this.overlay) {
        if (overlayActive) {
          this.overlay.classList.add('is-visible');
        } else {
          this.overlay.classList.remove('is-visible');
        }
      }
      if (this.navBackdrop) {
        if (this.isNavOpen) {
          this.navBackdrop.classList.add('is-visible');
        } else {
          this.navBackdrop.classList.remove('is-visible');
        }
      }
      const shouldLock = this.isDrawerOpen || this.isNavOpen;
      this.syncScrollLockState(shouldLock);
      this.applyAnnouncementShift(announcementActive);
      syncOverlayIndicatorClasses();
    }

    syncScrollLockState(shouldLock) {
      const root = document.documentElement;
      const body = document.body;
      if (!root || !body || !root.classList || !body.classList) {
        return;
      }

      let othersLocked = false;
      instances.forEach((instance) => {
        if (othersLocked) return;
        if (instance === this) return;
        if (instance.isDrawerOpen || instance.isNavOpen) {
          othersLocked = true;
        }
      });

      const lockActive = shouldLock || othersLocked;

      if (lockActive) {
        root.classList.add('has-colorful-overlay');
        body.classList.add('colorful-header-scroll-locked');
      } else {
        root.classList.remove('has-colorful-overlay');
        body.classList.remove('colorful-header-scroll-locked');
      }
    }

    addEscapeListener() {
      if (this.escapeAttached) return;
      document.addEventListener('keyup', this.onKeyUp);
      this.escapeAttached = true;
    }

    removeEscapeListener() {
      if (this.isDrawerOpen || this.isNavOpen || this.isMegaOpen) return;
      if (!this.escapeAttached) return;
      document.removeEventListener('keyup', this.onKeyUp);
      this.escapeAttached = false;
    }

    setHeaderHeight() {
      const measurementTarget = this.section.querySelector('.colorful-header__container');
      const target = measurementTarget instanceof HTMLElement ? measurementTarget : this.section;
      const rect = target.getBoundingClientRect();
      let measured = Math.round(rect.height);
      const viewportHeight =
        typeof window !== 'undefined' && typeof window.innerHeight === 'number'
          ? Math.round(window.innerHeight)
          : null;

      if (viewportHeight && measured >= viewportHeight) {
        measured = 0;
      }

      let value = measured > 0 ? `${measured}px` : '';

      if (!value && typeof window.getComputedStyle === 'function') {
        const computed = window.getComputedStyle(this.section).getPropertyValue('--colorful-header-height');
        if (computed) {
          value = computed.trim();
        }
      }

      if (!value) {
        value = DEFAULT_HEADER_HEIGHT;
      }

      this.section.style.setProperty('--colorful-header-height', value);
      document.documentElement.style.setProperty('--colorful-header-height', value);

      const offsetValue = this.calculateContentOffset(value);
      document.documentElement.style.setProperty('--colorful-header-offset', offsetValue);
      if (document.body) {
        document.body.style.setProperty('--colorful-header-offset', offsetValue);
      }
      this.section.style.setProperty('--colorful-header-offset', offsetValue);
      this.updateStackHeight(value);
      this.updateDrawerOffset();
      const overlayActive = this.isDrawerOpen || this.isMegaOpen;
      const announcementActive = overlayActive || this.isNavOpen;
      this.applyAnnouncementShift(announcementActive);
      this.syncOverlayVariables();

    }

    updateDrawerOffset() {
      if (!this.drawer) {
        return;
      }

      if (typeof window === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const viewportWidth = root ? root.clientWidth || window.innerWidth : window.innerWidth;
      if (!viewportWidth) {
        return;
      }

      let offset = 0;
      if (this.actions instanceof HTMLElement) {
        const rect = this.actions.getBoundingClientRect();
        if (rect && typeof rect.right === 'number') {
          offset = Math.max(0, Math.round(viewportWidth - rect.right));
        }
      }

      if (!offset && this.container instanceof HTMLElement) {
        const rect = this.container.getBoundingClientRect();
        if (rect && typeof rect.right === 'number') {
          offset = Math.max(0, Math.round(viewportWidth - rect.right));
        }
      }

      const value = `${offset}px`;
      if (this.lastDrawerOffset === value) {
        return;
      }

      this.lastDrawerOffset = value;
      this.section.style.setProperty('--cfs-drawer-inline-offset', value);
    }

    updateStackHeight(heightValue) {
      const fallback = this.lastHeaderHeight || DEFAULT_HEADER_HEIGHT_NUMERIC;
      const headerHeight = this.parseSize(heightValue, fallback);
      this.lastHeaderHeight = headerHeight;

      const announcementHeight = this.getAnnouncementHeight();
      const stackHeight = Math.max(0, headerHeight + announcementHeight);
      const stackValue = `${stackHeight}px`;
      const body = document.body || null;
      const needsBodyUpdate = Boolean(body) && (!this.bodyStackApplied || this.lastStackHeight !== stackValue);

      if (this.lastStackHeight === stackValue && !needsBodyUpdate) {
        return;
      }

      this.section.style.setProperty('--colorful-header-stack-height', stackValue);
      document.documentElement.style.setProperty('--colorful-header-stack-height', stackValue);
      if (body) {
        body.style.setProperty('--colorful-header-stack-height', stackValue);
        this.bodyStackApplied = true;
      }
      this.lastStackHeight = stackValue;
    }

    syncOverlayVariables() {
      if (typeof window === 'undefined') {
        return;
      }

      let computed = null;
      try {
        computed = window.getComputedStyle(this.section);
      } catch (error) {
        computed = null;
      }

      if (!computed) {
        return;
      }

      const root = typeof document !== 'undefined' ? document.documentElement : null;
      const blurRaw = computed.getPropertyValue('--cfs-overlay-blur');
      if (blurRaw) {
        const normalizedBlur = blurRaw.trim();
        if (normalizedBlur && normalizedBlur !== this.lastOverlayBlur) {
          if (root && root.style) {
            root.style.setProperty('--colorful-overlay-blur', normalizedBlur);
            root.style.setProperty('--halo-drawer-overlay-backdrop', `blur(${normalizedBlur})`);
          }
          this.lastOverlayBlur = normalizedBlur;
        }
      } else if (this.lastOverlayBlur !== null) {
        if (root && root.style) {
          root.style.removeProperty('--colorful-overlay-blur');
          root.style.removeProperty('--halo-drawer-overlay-backdrop');
        }
        this.lastOverlayBlur = null;
      }

      const colorRaw = computed.getPropertyValue('--cfs-overlay-rgb');
      const overlayOpacityRaw = computed.getPropertyValue('--cfs-overlay-opacity');
      const navOpacityRaw = computed.getPropertyValue('--cfs-nav-overlay-opacity');

      const normalizedColor = colorRaw ? colorRaw.trim() : '';
      const normalizedOverlayOpacity = overlayOpacityRaw ? overlayOpacityRaw.trim() : '';
      const normalizedNavOpacity = navOpacityRaw ? navOpacityRaw.trim() : '';

      if (root && root.style) {
        if (normalizedColor && normalizedColor !== this.lastOverlayColor) {
          root.style.setProperty('--colorful-overlay-rgb', normalizedColor);
          this.lastOverlayColor = normalizedColor;
        } else if (!normalizedColor && this.lastOverlayColor !== null) {
          root.style.removeProperty('--colorful-overlay-rgb');
          this.lastOverlayColor = null;
        }

        if (normalizedOverlayOpacity && normalizedOverlayOpacity !== this.lastOverlayOpacity) {
          root.style.setProperty('--colorful-overlay-opacity', normalizedOverlayOpacity);
          this.lastOverlayOpacity = normalizedOverlayOpacity;
        } else if (!normalizedOverlayOpacity && this.lastOverlayOpacity !== null) {
          root.style.removeProperty('--colorful-overlay-opacity');
          this.lastOverlayOpacity = null;
        }

        if (normalizedNavOpacity && normalizedNavOpacity !== this.lastNavOverlayOpacity) {
          root.style.setProperty('--colorful-nav-overlay-opacity', normalizedNavOpacity);
          this.lastNavOverlayOpacity = normalizedNavOpacity;
        } else if (!normalizedNavOpacity && this.lastNavOverlayOpacity !== null) {
          root.style.removeProperty('--colorful-nav-overlay-opacity');
          this.lastNavOverlayOpacity = null;
        }

        const overlayBackground = normalizedColor && normalizedOverlayOpacity
          ? `rgba(${normalizedColor}, ${normalizedOverlayOpacity})`
          : '';
        const navOverlayBackground = normalizedColor && (normalizedNavOpacity || normalizedOverlayOpacity)
          ? `rgba(${normalizedColor}, ${normalizedNavOpacity || normalizedOverlayOpacity})`
          : '';

        if (overlayBackground && overlayBackground !== this.lastOverlayBackground) {
          root.style.setProperty('--halo-overlay-background', overlayBackground);
          this.lastOverlayBackground = overlayBackground;
        } else if (!overlayBackground && this.lastOverlayBackground !== null) {
          root.style.removeProperty('--halo-overlay-background');
          this.lastOverlayBackground = null;
        }

        if (navOverlayBackground && navOverlayBackground !== this.lastNavOverlayBackground) {
          root.style.setProperty('--halo-drawer-overlay-bg', navOverlayBackground);
          this.lastNavOverlayBackground = navOverlayBackground;
        } else if (!navOverlayBackground && this.lastNavOverlayBackground !== null) {
          root.style.removeProperty('--halo-drawer-overlay-bg');
          this.lastNavOverlayBackground = null;
        }
      }

    }
    getAnnouncementHeight() {
      const previous = this.lastAnnouncementHeight || 0;

      if (typeof window === 'undefined') {
        return previous;
      }

      const root = document.documentElement;
      if (!root) {
        return previous;
      }

      if (typeof window.getComputedStyle !== 'function') {
        return previous;
      }

      let announcementHeight = previous;
      try {
        const computed = window.getComputedStyle(root);
        if (computed) {
          const value = computed.getPropertyValue('--announcement-bar-height');
          announcementHeight = this.parseSize(value, announcementHeight);
        }
      } catch (error) {
        return previous;
      }

      this.lastAnnouncementHeight = announcementHeight;
      return announcementHeight;
    }

    parseSize(value, fallback = 0) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string') {
        const normalized = value.trim();
        if (normalized) {
          const parsed = parseFloat(normalized);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
      }

      return fallback;
    }

    applyAnnouncementShift(active) {
      const root = document.documentElement;
      if (root && root.classList) {
        root.classList.toggle('colorful-header--announcement-collapsed', Boolean(active));
      }
      if (this.headerGroup && this.headerGroup.classList) {
        this.headerGroup.classList.toggle('colorful-header--announcement-collapsed', Boolean(active));
      }
    }

    calculateContentOffset(defaultValue) {
      const firstSection = this.getFirstContentSection();
      const shouldOverlap = this.shouldSectionOverlap(firstSection);
      const allowMobileOverlap = this.allowMobileOverlap(firstSection, shouldOverlap);
      const overlapActive = shouldOverlap && (this.mql.matches || allowMobileOverlap);
      this.toggleOverlapClasses(overlapActive);
      return overlapActive ? '0px' : defaultValue;
    }

    allowMobileOverlap(section, overlapHint = false) {
      if (!section || !(section instanceof HTMLElement)) {
        return false;
      }

      const directPreference = this.getMobileOverlapPreference(section);
      if (directPreference !== null) {
        return directPreference;
      }

      const descendant = section.querySelector(
        '[data-header-mobile-overlap],[data-header-overlap-mobile],[data-mobile-header-overlap]'
      );
      if (descendant) {
        const descendantPreference = this.getMobileOverlapPreference(descendant);
        if (descendantPreference !== null) {
          return descendantPreference;
        }
      }

      if (overlapHint) {
        return true;
      }

      return false;
    }

    getMobileOverlapPreference(element) {
      if (!element || !(element instanceof HTMLElement)) {
        return null;
      }

      const dataset = element.dataset || {};
      if (
        dataset.headerMobileOverlap === 'false' ||
        dataset.headerOverlapMobile === 'false' ||
        dataset.mobileHeaderOverlap === 'false'
      ) {
        return false;
      }

      if (
        dataset.headerMobileOverlap === 'true' ||
        dataset.headerOverlapMobile === 'true' ||
        dataset.mobileHeaderOverlap === 'true'
      ) {
        return true;
      }

      if (element.hasAttribute('data-header-mobile-overlap')) {
        return element.getAttribute('data-header-mobile-overlap') !== 'false';
      }

      if (element.hasAttribute('data-header-overlap-mobile')) {
        return element.getAttribute('data-header-overlap-mobile') !== 'false';
      }

      if (element.hasAttribute('data-mobile-header-overlap')) {
        return element.getAttribute('data-mobile-header-overlap') !== 'false';
      }

      return null;
    }

    getFirstContentSection() {
      const main = document.getElementById('MainContent');
      if (!main) return null;

      const directSections = Array.from(main.children || []).filter(
        (node) => node instanceof HTMLElement && node.classList.contains('shopify-section')
      );

      if (directSections.length) {
        const visible = directSections.find((section) => this.isSectionVisible(section));
        return visible || directSections[0];
      }

      const fallback = main.querySelector('.shopify-section');
      return fallback instanceof HTMLElement ? fallback : null;
    }

    isSectionVisible(section) {
      if (!section || !(section instanceof HTMLElement)) return false;
      if (section.hasAttribute('hidden')) return false;
      if (section.getAttribute('aria-hidden') === 'true') return false;
      if (section.dataset) {
        if (section.dataset.sectionHidden === 'true' || section.dataset.shopifyHidden === 'true') {
          return false;
        }
      }

      if (typeof window.getComputedStyle === 'function') {
        const style = window.getComputedStyle(section);
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false;
        }
      }

      return section.offsetWidth > 0 && section.offsetHeight > 0;
    }

    shouldSectionOverlap(section) {
      if (!section || !(section instanceof HTMLElement)) {
        return false;
      }

      if (section.dataset) {
        if (section.dataset.disableHeaderOverlap === 'true') {
          return false;
        }
        if (section.dataset.headerOverlap === 'false') {
          return false;
        }
        if (section.dataset.headerOverlap === 'true') {
          return true;
        }
      }

      if (section.classList.contains('colorful-header--prevent-overlap')) {
        return false;
      }

      if (section.classList.contains('colorful-header--force-overlap')) {
        return true;
      }

      if (section.hasAttribute('data-header-overlap')) {
        return section.getAttribute('data-header-overlap') !== 'false';
      }

      const descendant = section.querySelector('[data-header-overlap]');
      if (descendant && descendant.getAttribute('data-header-overlap') !== 'false') {
        return true;
      }

      const type = (section.dataset && section.dataset.sectionType ? section.dataset.sectionType : '').toLowerCase();
      if (type && OVERLAP_SECTION_KEYWORDS.some((keyword) => type.includes(keyword))) {
        return true;
      }

      const identifier = (section.id || '').toLowerCase();
      if (identifier && OVERLAP_SECTION_KEYWORDS.some((keyword) => identifier.includes(keyword))) {
        return true;
      }

      const directChildren = Array.from(section.children || []).filter((child) => child instanceof HTMLElement);
      if (
        directChildren.some((child) => {
          const className = typeof child.className === 'string' ? child.className.toLowerCase() : '';
          if (!className) return false;
          return OVERLAP_CHILD_KEYWORDS.some((keyword) => className.includes(keyword));
        })
      ) {
        return true;
      }

      if (OVERLAP_MEDIA_SELECTOR && section.querySelector(OVERLAP_MEDIA_SELECTOR)) {
        return true;
      }

      return false;
    }

    toggleOverlapClasses(active) {
      this.lastOverlapState = active;
      this.section.classList.toggle('colorful-header--overlap', active);
      const root = document.documentElement;
      root.classList.toggle('colorful-header-overlap', active);
      if (document.body) {
        document.body.classList.toggle('colorful-header-overlap', active);
      }
    }

    destroy() {
      this.toggleOverlapClasses(false);
      this.isDrawerOpen = false;
      this.isNavOpen = false;
      this.isMegaOpen = false;
      if (this.section && this.section.classList) {
        this.section.classList.remove('colorful-header--drawer-open', 'colorful-header--mega-open');
        if (this.section.dataset) {
          this.section.dataset.navOpen = 'false';
        }
      }
      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
      }
      if (this.navBackdrop) {
        this.navBackdrop.classList.remove('is-visible');
      }
      window.removeEventListener('resize', this.onResize);
      if (typeof this.mql.removeEventListener === 'function') {
        this.mql.removeEventListener('change', this.onMediaChange);
      } else if (typeof this.mql.removeListener === 'function') {
        this.mql.removeListener(this.onMediaChange);
      }
      document.removeEventListener('cart-update', this.onCartUpdate);
      this.detachCartFeatures();
      this.pendingCartRender = null;
      this.isRenderingCart = false;
      this.isUpdatingCartQuantity = false;
      if (this.navToggle) {
        this.navToggle.removeEventListener('click', this.onNavToggle);
      }
      if (this.navBackdrop) {
        this.navBackdrop.removeEventListener('click', this.onNavBackdrop);
      }
      this.drawerTriggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.onDrawerTrigger);
      });
      if (this.drawerClose) {
        this.drawerClose.removeEventListener('click', this.onDrawerClose);
      }
      if (this.overlay) {
        this.overlay.removeEventListener('click', this.onOverlayClick);
      }
      this.suggestionLinks.forEach((link) => {
        link.removeEventListener('click', this.onSuggestionClick);
      });
      this.detachFocusTrap();
      this.megaConfigs.forEach(({
        item,
        trigger,
        panel,
        clickHandler,
        keyHandler,
        focusOut,
        hoverHandler,
        leaveHandler,
        focusHandler
      }) => {
        if (trigger) {
          trigger.removeEventListener('click', clickHandler);
          trigger.removeEventListener('keydown', keyHandler);
          trigger.removeEventListener('focus', focusHandler);
        }
        if (panel) {
          panel.removeEventListener('focusout', focusOut);
          panel.style.removeProperty('--mega-shift');
        }
        if (item) {
          item.removeEventListener('mouseenter', hoverHandler);
          item.removeEventListener('mouseleave', leaveHandler);
          item.classList.remove('is-open');
        }
      });
      if (this.megaConfigs.length) {
        document.removeEventListener('click', this.onDocumentClick);
      }
      this.removeEscapeListener();
      this.syncScrollLockState(false);
      syncOverlayIndicatorClasses();
    }
  }

  function initSection(section) {
    if (!section) return;
    const instance = new ColorfulHeader(section);
    instances.set(instance.id, instance);
    updateGlobalDrawerBridge();
    syncOverlayIndicatorClasses();
  }

  function destroySectionById(sectionId) {
    const instance = instances.get(sectionId);
    if (instance) {
      instance.destroy();
      instances.delete(sectionId);
    }
    updateGlobalDrawerBridge();
    syncOverlayIndicatorClasses();
  }

  function handleReady() {
    document.querySelectorAll(SELECTOR).forEach((section) => initSection(section));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleReady);
  } else {
    handleReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    if (!sectionId) return;
    const section = document.querySelector(`${SELECTOR}[data-section-id="${sectionId}"]`);
    if (section) {
      initSection(section);
    }
    instances.forEach((instance) => instance.setHeaderHeight());
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    if (!sectionId) return;
    destroySectionById(sectionId);
    instances.forEach((instance) => instance.setHeaderHeight());
  });

  document.addEventListener('shopify:section:reorder', () => {
    instances.forEach((instance) => instance.setHeaderHeight());
  });

  document.addEventListener('announcement-bar:resize', () => {
    instances.forEach((instance) => instance.setHeaderHeight());
  });
})();
